/**
 * Auto-Approve Interrupt Hook
 *
 * Provides auto-approve functionality for single-section suggestions.
 * Only applies to global namespace + note_changes confirmationType CONFIRMATION interrupts
 * with exactly 1 affected section.
 *
 * Prevents duplicate approvals by tracking which interrupt has already been auto-approved.
 * Also handles auto-approve on component unmount.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAppSelector } from '@store/hooks';

import {
  selectAffectedSectionIds,
  selectCurrentInterruptId,
  selectInterrupt,
} from '../store/selectors';

export interface UseAutoApproveInterruptParams {
  /** The approval handler function from useNoteChatWithSections */
  handleApproval: (confirmed: boolean, skipCacheUpdate?: boolean) => void;
}

export interface UseAutoApproveInterruptReturn {
  /** Whether auto-approve conditions are met */
  shouldAutoApprove: boolean;
  /** Function to trigger auto-approve (only works if shouldAutoApprove is true) */
  autoApprove: (skipCacheUpdate?: boolean) => void;
}

/**
 * Hook for auto-approving single-section suggestions
 *
 * Conditions for auto-approve:
 * - Interrupt is active
 * - Event type is CONFIRMATION
 * - Namespace is 'global' (or undefined/null)
 * - Confirmation type is 'note_changes'
 * - Exactly 1 section is affected
 *
 * @param handleApproval - The approval handler from useNoteChatWithSections
 * @returns Object with shouldAutoApprove flag and autoApprove function
 */
export const useAutoApproveInterrupt = ({
  handleApproval,
}: UseAutoApproveInterruptParams): UseAutoApproveInterruptReturn => {
  const interrupt = useAppSelector(selectInterrupt);
  const affectedSectionIds = useAppSelector(selectAffectedSectionIds);
  const interruptId = useAppSelector(selectCurrentInterruptId);

  // Track which interrupt we've already auto-approved to prevent duplicates
  const approvedInterruptIdRef = useRef<string | null>(null);

  // Track refs for unmount cleanup (to avoid stale closures)
  const handleApprovalRef = useRef(handleApproval);

  const shouldAutoApprove =
    interrupt?.isActive === true &&
    interrupt?.eventType === 'CONFIRMATION' &&
    (!interrupt?.namespace || interrupt.namespace === 'global') &&
    interrupt?.confirmationType === 'note_changes' &&
    affectedSectionIds.length === 1;

  // Keep refs in sync
  useEffect(() => {
    handleApprovalRef.current = handleApproval;
  }, [handleApproval]);

  // Track shouldAutoApprove in ref for unmount
  const shouldAutoApproveRef = useRef(shouldAutoApprove);
  useEffect(() => {
    shouldAutoApproveRef.current = shouldAutoApprove;
  }, [shouldAutoApprove]);

  // Reset approved tracking when interrupt changes
  useEffect(() => {
    if (interruptId !== approvedInterruptIdRef.current) {
      approvedInterruptIdRef.current = null;
    }
  }, [interruptId]);

  const autoApprove = useCallback(
    (skipCacheUpdate = false) => {
      // Only approve if conditions are met and we haven't already approved this interrupt
      if (shouldAutoApprove && interruptId && approvedInterruptIdRef.current !== interruptId) {
        approvedInterruptIdRef.current = interruptId;
        handleApproval(true, skipCacheUpdate);
      }
    },
    [shouldAutoApprove, interruptId, handleApproval],
  );

  // Auto-approve on TRUE component unmount only (empty deps)
  useEffect(() => {
    return () => {
      if (shouldAutoApproveRef.current && approvedInterruptIdRef.current === null) {
        handleApprovalRef.current(true, false);
      }
    };
  }, []); // Empty deps - only runs on actual unmount

  return { shouldAutoApprove, autoApprove };
};
